import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { BRAND_ORANGE, multilineFieldSx } from './manageEventFieldStyles';
import {
  EXTRACT_EVENT_DETAILS_PROMPT_MAX,
  EVENT_DESCRIPTION_MIN_ROWS,
} from '../eventFormConstants';
import { useExtractEventDetailsMutation } from '../../../../api/use-extract-event-details-mutation';

const PROMPT_PLACEHOLDER =
  'e.g. Rooftop sound healing evening at Zabeel House, Fri Sep 5, 7–9pm, 40 seats, 120 AED';

const EventDescribePrompt = ({ onApply }) => {
  const [prompt, setPrompt] = useState('');
  const extractEventDetailsMutation = useExtractEventDetailsMutation();
  const isGenerating = extractEventDetailsMutation.isPending;

  const handleGenerate = async () => {
    const text = prompt.trim();
    if (!text || isGenerating) return;

    try {
      const result = await extractEventDetailsMutation.mutateAsync(text);
      onApply?.(result.formPatch);
      toast.success(
        result.message || 'Event details added. Review the form and complete any remaining fields.',
      );
    } catch (error) {
      toast.error(error?.message || 'Failed to extract event details.');
    }
  };

  return (
    <Box
      sx={{
        mb: 3,
        px: 2,
        py: 1.75,
        borderRadius: '12px',
        border: '1px solid',
        borderColor: (theme) => theme.portal?.accentMuted ?? theme.palette.divider,
        bgcolor: (theme) => theme.portal?.accentSoft ?? 'action.hover',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
        <AutoAwesomeIcon sx={{ fontSize: 18, color: BRAND_ORANGE }} />
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' }}>
          Describe your event, and we&apos;ll fill in the form
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          gap: 1.25,
        }}
      >
        <TextField
          fullWidth
          multiline
          minRows={EVENT_DESCRIPTION_MIN_ROWS}
          maxRows={EVENT_DESCRIPTION_MIN_ROWS}
          value={prompt}
          onChange={(event) => setPrompt(event.target.value)}
          placeholder={PROMPT_PLACEHOLDER}
          disabled={isGenerating}
          inputProps={{ maxLength: EXTRACT_EVENT_DETAILS_PROMPT_MAX }}
          sx={multilineFieldSx}
        />
        <Button
          variant="contained"
          disableElevation
          onClick={handleGenerate}
          disabled={!prompt.trim() || isGenerating}
          sx={{
            flexShrink: 0,
            minWidth: 120,
            height: 48,
            px: 3,
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '10px',
            bgcolor: BRAND_ORANGE,
            '&:hover': { bgcolor: 'primary.dark' },
            '&.Mui-disabled': {
              bgcolor: 'primary.light',
              color: 'common.white',
            },
          }}
        >
          {isGenerating ? (
            <CircularProgress size={20} sx={{ color: 'common.white' }} />
          ) : (
            'Generate'
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default EventDescribePrompt;
